import { createContext, useContext, useState, useCallback, type FC, type ReactNode } from 'react';
import { ConfigProvider } from 'antd';
import { uvpTheme, uvpDarkTheme } from '@/styles/uvp-theme';

type ThemeMode = 'light' | 'dark';

interface ThemeContextValue {
  themeMode: ThemeMode;
  toggleTheme: () => void;
  isDark: boolean;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: FC<ThemeProviderProps> = ({ children }) => {
  const [themeMode, setThemeMode] = useState<ThemeMode>(() => {
    const saved = localStorage.getItem('uvp-theme');
    return saved === 'dark' ? 'dark' : 'light';
  });

  const isDark = themeMode === 'dark';

  const toggleTheme = useCallback(() => {
    setThemeMode(prev => {
      const next = prev === 'light' ? 'dark' : 'light';
      localStorage.setItem('uvp-theme', next);
      return next;
    });
  }, []);

  const themeConfig = isDark ? uvpDarkTheme : uvpTheme;

  return (
    <ThemeContext.Provider value={{ themeMode, toggleTheme, isDark }}>
      <ConfigProvider theme={themeConfig}>{children}</ConfigProvider>
    </ThemeContext.Provider>
  );
};
